import { useParams } from 'react-router-dom';

import { Icon, type IconName, Stack, Text } from '@eluve/components';

export type ToolInvocation = {
  toolCallId: string;
  toolName: string;
  state: 'input-streaming' | 'input-available' | 'output-available';
  input?: unknown;
  output?: unknown;
};

interface NoteEntry {
  sourceTimestamp?: string;
  appointmentId?: string;
  appointmentName?: string;
}

interface ToolDisplayConfig {
  activeLabel: string;
  completedLabel: string;
  icon: IconName;
}

const toolConfig: Record<string, ToolDisplayConfig> = {
  getPatientNotes: {
    activeLabel: 'Searching patient records',
    completedLabel: 'Found patient records',
    icon: 'Search',
  },
  createSpecTemplate: {
    activeLabel: 'Building template preview',
    completedLabel: 'Template ready',
    icon: 'WandSparkles',
  },
  saveSpecTemplate: {
    activeLabel: 'Saving template',
    completedLabel: 'Template saved',
    icon: 'Save',
  },
  draftDocument: {
    activeLabel: 'Drafting document',
    completedLabel: 'Document draft ready',
    icon: 'FileText',
  },
  generateDocument: {
    activeLabel: 'Saving document',
    completedLabel: 'Document saved',
    icon: 'FileText',
  },
  getAppointmentNote: {
    activeLabel: 'Reading appointment note',
    completedLabel: 'Appointment note loaded',
    icon: 'FileText',
  },
  updateAppointmentNote: {
    activeLabel: 'Updating appointment note',
    completedLabel: 'Appointment note updated',
    icon: 'FileEdit',
  },
  getCustomInstructions: {
    activeLabel: 'Reading custom instructions',
    completedLabel: 'Custom instructions loaded',
    icon: 'Settings',
  },
  suggestCustomInstruction: {
    activeLabel: 'Preparing suggestion',
    completedLabel: 'Suggestion ready',
    icon: 'Settings',
  },
  saveCustomInstruction: {
    activeLabel: 'Saving custom instruction',
    completedLabel: 'Custom instruction saved',
    icon: 'Save',
  },
  queryFhirResources: {
    activeLabel: 'Searching clinical records',
    completedLabel: 'Found clinical records',
    icon: 'Stethoscope',
  },
  // Supervisor delegation tool calls (agent-{key} from agents map)
  'agent-patientNotesAgent': {
    activeLabel: 'Asking Patient Notes Agent',
    completedLabel: 'Patient Notes Agent responded',
    icon: 'Search',
  },
  'agent-conversationStarterAgent': {
    activeLabel: 'Asking Starter Agent',
    completedLabel: 'Starter Agent responded',
    icon: 'MessageSquarePlus',
  },
};

const defaultToolConfig: ToolDisplayConfig = {
  activeLabel: 'Working',
  completedLabel: 'Done',
  icon: 'WandSparkles',
};

function getToolConfig(toolName: string): ToolDisplayConfig {
  return toolConfig[toolName] ?? defaultToolConfig;
}

function getToolLabel(toolInvocation: ToolInvocation): string {
  const config = getToolConfig(toolInvocation.toolName);
  return toolInvocation.state === 'output-available'
    ? config.completedLabel
    : config.activeLabel;
}

function getRetrievedNotes(toolInvocation: ToolInvocation): NoteEntry[] | null {
  if (
    toolInvocation.toolName !== 'getPatientNotes' ||
    toolInvocation.state !== 'output-available'
  ) {
    return null;
  }
  try {
    const output = toolInvocation.output;
    if (Array.isArray(output)) {
      return output.filter(
        (n: NoteEntry) => n.sourceTimestamp ?? n.appointmentId,
      );
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export const ToolCallStatus = ({
  toolInvocation,
}: {
  toolInvocation: ToolInvocation;
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const isComplete = toolInvocation.state === 'output-available';
  const label = getToolLabel(toolInvocation);
  const config = getToolConfig(toolInvocation.toolName);
  const retrievedNotes = getRetrievedNotes(toolInvocation);

  return (
    <Stack gap={1}>
      <div
        className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 transition-all duration-300 ${
          isComplete
            ? 'border-border-positive-subtle bg-bg-positive-subtle'
            : 'border-border-insights-subtle bg-bg-insights-subtle'
        }`}
      >
        {isComplete ? (
          <Icon name="CheckCircle2" className="text-text-positive" />
        ) : (
          <Icon
            name={config.icon}
            className="animate-pulse text-text-insights"
          />
        )}
        <Text size="xs" color={isComplete ? 'secondary' : 'primary'}>
          {label}
          {!isComplete && '…'}
        </Text>
      </div>
      {retrievedNotes && retrievedNotes.length > 0 && (
        <Stack className="mt-0.5 ml-4">
          {retrievedNotes.map((note, i) => {
            const date = note.sourceTimestamp
              ? new Date(note.sourceTimestamp).toLocaleDateString()
              : null;
            const name = note.appointmentName ?? null;
            const noteLabel =
              name && date
                ? `${name} - ${date}`
                : name ?? date ?? 'Appointment note';
            const href =
              tenantId && note.appointmentId
                ? `/organizations/${tenantId}/appointments/${note.appointmentId}`
                : undefined;

            return href ? (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-text-tertiary underline hover:text-text-primary"
              >
                {noteLabel}
              </a>
            ) : (
              <Text inline key={i} size="xs" color="tertiary">
                {noteLabel}
              </Text>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
